// src/components/songEdit/hooks/useSongEditState.ts
import { useState } from 'react';
import { SongFormData, PendingVersion } from '../types/songEdit';

const initialSongForm: SongFormData = {
  title: '',
  composer: '',
  lyricist: '',
  composerData: undefined,
  lyricistData: undefined,
  primaryTune: '',
  alternateTunes: [],
  tags: [],
  video_link: '',
  copyright: '',
  language: 'en',
  pendingVersions: []
};


export const useSongEditState = () => {
  const [showSongForm, setShowSongForm] = useState(false);
  const [formData, setFormData] = useState<SongFormData>(initialSongForm);
  const [initialFormData, setInitialFormData] = useState<SongFormData>(initialSongForm);
  const [isDirty, setIsDirty] = useState(false);

  const handleFormChange = (data: SongFormData) => {
    setFormData(data);
    setIsDirty(true);
  };

  const resetForms = () => {
    setFormData(initialSongForm);
    setInitialFormData(initialSongForm);
    setIsDirty(false);
  };

  const handleSaveSuccess = () => {
    resetForms();
    setShowSongForm(false);  // Hide the form after save
  };

  const loadSongData = (data: SongFormData) => {
    // Clear pending versions when loading existing song (versions come from API)
    setFormData({ ...data, pendingVersions: [] });
    setInitialFormData({ ...data, pendingVersions: [] });
    setShowSongForm(true);
    setIsDirty(false);
  };

  // Pending version management for new songs
  const addPendingVersion = (version: Omit<PendingVersion, 'tempId'>) => {
    const newVersion: PendingVersion = {
      ...version,
      tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      is_default: formData.pendingVersions?.length === 0 // First version is default
    };
    setFormData(prev => ({
      ...prev,
      pendingVersions: [...(prev.pendingVersions || []), newVersion]
    }));
    setIsDirty(true);
  };

  const updatePendingVersion = (tempId: string, updates: Partial<PendingVersion>) => {
    setFormData(prev => ({
      ...prev,
      pendingVersions: (prev.pendingVersions || []).map(v =>
        v.tempId === tempId ? { ...v, ...updates } : v
      )
    }));
    setIsDirty(true);
  };

  const removePendingVersion = (tempId: string) => {
    setFormData(prev => {
      const updated = (prev.pendingVersions || []).filter(v => v.tempId !== tempId);
      // If we removed the default, make the first remaining version default
      if (updated.length > 0 && !updated.some(v => v.is_default)) {
        updated[0].is_default = true;
      }
      return { ...prev, pendingVersions: updated };
    });
    setIsDirty(true);
  };

  const setPendingVersionDefault = (tempId: string) => {
    setFormData(prev => ({
      ...prev,
      pendingVersions: (prev.pendingVersions || []).map(v => ({
        ...v,
        is_default: v.tempId === tempId
      }))
    }));
    setIsDirty(true);
  };

  return {
    showSongForm,
    setShowSongForm,
    formData,
    setFormData,
    initialFormData,
    isDirty,
    setIsDirty,
    resetForms,
    handleSaveSuccess,
    loadSongData,
    handleFormChange,
    // Pending version management
    addPendingVersion,
    updatePendingVersion,
    removePendingVersion,
    setPendingVersionDefault
  };
};